/**
 * Aba Licenças — AFE, AE, sanitárias, alvará.
 *
 * Lista todas com badge de validade. Validade próxima vira alerta visual:
 *   - vencida          → vermelho
 *   - vence em ≤15 dias → vermelho
 *   - vence em ≤30 dias → amarelo
 *   - vence em ≤60 dias → cinza claro
 *   - vence depois     → sem destaque
 *
 * Soft delete (is_active=false) preserva histórico.
 */

import { useEffect, useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import {
  AlertTriangle,
  Calendar,
  FileBadge,
  Pencil,
  Plus,
  Trash2,
} from "lucide-react";
import toast from "react-hot-toast";
import clsx from "clsx";

import { tenantsCompanyApi } from "@/api/tenantsCompany";
import { apiErrorMessage } from "@/api/client";
import { qk, useTenantLicencas } from "@/hooks/queries";
import type {
  TenantLicenca,
  TenantLicencaCreate,
  TenantLicencaUpdate,
  TipoLicenca,
} from "@/types";

import { Badge } from "@/components/Badge";
import { Button } from "@/components/Button";
import { Card, CardBody, CardHeader } from "@/components/Card";
import { ConfirmDialog } from "@/components/ConfirmDialog";
import { Field, Input, Select } from "@/components/Field";
import { Modal } from "@/components/Modal";
import { Skeleton } from "@/components/Skeleton";
import { Tip } from "@/components/Tip";
import { formatDate } from "@/lib/format";

const TIPO_OPTIONS: { value: TipoLicenca; label: string }[] = [
  { value: "afe",                  label: "AFE — Autorização de Funcionamento (ANVISA)" },
  { value: "ae",                   label: "AE — Autorização Especial (ANVISA)" },
  { value: "sanitaria_municipal",  label: "Licença Sanitária Municipal" },
  { value: "sanitaria_estadual",   label: "Licença Sanitária Estadual" },
  { value: "alvara",               label: "Alvará Sanitário" },
  { value: "outro",                label: "Outro" },
];

const TIPO_SHORT: Record<TipoLicenca, string> = {
  afe:                 "AFE",
  ae:                  "AE",
  sanitaria_municipal: "Sanitária Mun.",
  sanitaria_estadual:  "Sanitária Est.",
  alvara:              "Alvará",
  outro:               "Outro",
};

// ---------- Tab principal ----------

export function LicencasTab({ tenantId }: { tenantId: string }) {
  const [includeInactive, setIncludeInactive] = useState(false);
  const list = useTenantLicencas(tenantId, includeInactive);
  const qc = useQueryClient();

  const [isCreating, setIsCreating] = useState(false);
  const [editTarget, setEditTarget] = useState<TenantLicenca | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<TenantLicenca | null>(null);

  const invalidate = () => {
    void qc.invalidateQueries({ queryKey: qk.tenantLicencas(tenantId, false) });
    void qc.invalidateQueries({ queryKey: qk.tenantLicencas(tenantId, true) });
    void qc.invalidateQueries({ queryKey: qk.tenantAuditLogs(tenantId) });
  };

  const createMut = useMutation({
    mutationFn: (p: TenantLicencaCreate) =>
      tenantsCompanyApi.createLicenca(tenantId, p),
    onSuccess: () => {
      toast.success("Licença adicionada");
      setIsCreating(false);
      invalidate();
    },
    onError: (e) => toast.error(apiErrorMessage(e)),
  });

  const updateMut = useMutation({
    mutationFn: ({ id, p }: { id: string; p: TenantLicencaUpdate }) =>
      tenantsCompanyApi.updateLicenca(tenantId, id, p),
    onSuccess: () => {
      toast.success("Licença atualizada");
      setEditTarget(null);
      invalidate();
    },
    onError: (e) => toast.error(apiErrorMessage(e)),
  });

  const deleteMut = useMutation({
    mutationFn: (id: string) => tenantsCompanyApi.deactivateLicenca(tenantId, id),
    onSuccess: () => {
      toast.success("Licença desativada");
      setDeleteTarget(null);
      invalidate();
    },
    onError: (e) => toast.error(apiErrorMessage(e)),
  });

  if (list.isLoading) return <Skeleton className="h-48 w-full" />;

  const items = list.data ?? [];

  // Conta vencidas / próximas pra dar overview no topo
  const summary = items.reduce(
    (acc, l) => {
      if (!l.is_active) return acc;
      const days = daysUntilExpiry(l.data_validade);
      if (days === null) return acc;
      if (days < 0)        acc.expired++;
      else if (days <= 15) acc.critical++;
      else if (days <= 30) acc.warning++;
      else if (days <= 60) acc.upcoming++;
      return acc;
    },
    { expired: 0, critical: 0, warning: 0, upcoming: 0 },
  );

  const hasAlerts = summary.expired + summary.critical + summary.warning > 0;

  return (
    <div className="space-y-4">
      {/* Banner de alertas (se houver) */}
      {hasAlerts && (
        <Card className="border-amber-200 dark:border-amber-900/40 !bg-amber-50 dark:!bg-amber-950/30">
          <CardBody className="py-3 text-sm text-amber-900 dark:text-amber-200">
            <div className="flex items-center gap-2">
              <AlertTriangle className="h-4 w-4 flex-none text-amber-600 dark:text-amber-400" />
              <span>
                Atenção:
                {summary.expired  > 0 && <span className="ml-2 font-medium text-red-700 dark:text-red-300">{summary.expired} vencida{summary.expired > 1 && "s"}</span>}
                {summary.critical > 0 && <span className="ml-2 font-medium text-red-700 dark:text-red-300">{summary.critical} vence{summary.critical > 1 && "m"} em ≤15d</span>}
                {summary.warning  > 0 && <span className="ml-2 font-medium text-amber-700 dark:text-amber-300">{summary.warning} vence{summary.warning > 1 && "m"} em ≤30d</span>}
              </span>
            </div>
          </CardBody>
        </Card>
      )}

      <Card>
        <CardHeader
          title={
            <span className="inline-flex items-center gap-2">
              <FileBadge className="h-4 w-4 text-brand-600" /> Licenças e Autorizações
            </span>
          }
          subtitle="AFE, AE (ANVISA), licenças sanitárias e alvará"
          actions={
            <div className="flex items-center gap-3">
              <label className="flex items-center gap-2 text-xs text-slate-500 dark:text-slate-400">
                <input
                  type="checkbox"
                  checked={includeInactive}
                  onChange={(e) => setIncludeInactive(e.target.checked)}
                  className="h-4 w-4 rounded border-surface-300 dark:border-slate-700 text-brand-600"
                />
                Incluir inativas
              </label>
              <Button
                size="sm"
                variant="secondary"
                icon={<Plus className="h-4 w-4" />}
                onClick={() => setIsCreating(true)}
              >
                Nova licença
              </Button>
            </div>
          }
        />
        <CardBody>
          {items.length === 0 ? (
            <p className="py-6 text-center text-sm text-slate-500 dark:text-slate-400">
              Nenhuma licença cadastrada.
            </p>
          ) : (
            <ul className="space-y-2">
              {items.map((l) => (
                <LicencaRow
                  key={l.id}
                  l={l}
                  onEdit={() => setEditTarget(l)}
                  onDelete={() => setDeleteTarget(l)}
                />
              ))}
            </ul>
          )}
        </CardBody>
      </Card>

      <LicencaFormModal
        key={isCreating ? "create" : `edit-${editTarget?.id ?? "none"}`}
        open={isCreating || !!editTarget}
        onClose={() => {
          setIsCreating(false);
          setEditTarget(null);
        }}
        initial={editTarget}
        submitting={createMut.isPending || updateMut.isPending}
        onSubmit={(p) => {
          if (editTarget) {
            const { tipo: _, ...patch } = p;
            updateMut.mutate({ id: editTarget.id, p: patch });
          } else {
            createMut.mutate(p);
          }
        }}
      />

      <ConfirmDialog
        open={!!deleteTarget}
        title="Desativar licença?"
        message={
          deleteTarget
            ? `${TIPO_SHORT[deleteTarget.tipo]} ${deleteTarget.numero} será marcada como inativa. Pode reativar depois — histórico fica preservado.`
            : ""
        }
        confirmLabel="Desativar"
        loading={deleteMut.isPending}
        onCancel={() => setDeleteTarget(null)}
        onConfirm={() => deleteTarget && deleteMut.mutate(deleteTarget.id)}
      />
    </div>
  );
}

// ---------- Subcomponentes ----------

function LicencaRow({
  l,
  onEdit,
  onDelete,
}: {
  l: TenantLicenca;
  onEdit: () => void;
  onDelete: () => void;
}) {
  const days = daysUntilExpiry(l.data_validade);
  const validityState = classifyValidity(days);

  return (
    <li
      className={clsx(
        "flex items-start justify-between rounded-lg border bg-white dark:bg-slate-900 px-4 py-3",
        validityState.borderClass,
      )}
    >
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2">
          <Badge tone="brand">{TIPO_SHORT[l.tipo]}</Badge>
          <span className="font-medium text-slate-900 dark:text-slate-100">{l.numero}</span>
          {!l.is_active && <Badge tone="default">Inativa</Badge>}
          {l.is_active && validityState.label && (
            <Badge tone={validityState.tone}>{validityState.label}</Badge>
          )}
        </div>

        <div className="mt-1 grid gap-x-4 gap-y-0.5 text-xs text-slate-500 dark:text-slate-400 sm:grid-cols-2">
          {l.orgao_emissor && <span>Emissor: {l.orgao_emissor}</span>}
          {l.data_emissao && (
            <span className="inline-flex items-center gap-1">
              <Calendar className="h-3 w-3" /> Emissão: {formatDate(l.data_emissao, "dd/MM/yyyy")}
            </span>
          )}
          {l.data_validade && (
            <span className={clsx(
              "inline-flex items-center gap-1",
              validityState.textClass,
            )}>
              <Calendar className="h-3 w-3" />
              Validade: {formatDate(l.data_validade, "dd/MM/yyyy")}
              {days !== null && (
                <span className="ml-1 text-[10px]">
                  ({days < 0 ? `vencida há ${-days}d` : `${days}d restantes`})
                </span>
              )}
            </span>
          )}
        </div>

        {l.observacoes && (
          <p className="mt-2 text-xs italic text-slate-600 dark:text-slate-400">{l.observacoes}</p>
        )}
      </div>

      <div className="ml-3 flex items-center gap-1">
        {l.is_active && (
          <>
            <Button
              size="sm"
              variant="ghost"
              icon={<Pencil className="h-4 w-4" />}
              onClick={onEdit}
            />
            <Button
              size="sm"
              variant="ghost"
              icon={<Trash2 className="h-4 w-4 text-red-500" />}
              onClick={onDelete}
            />
          </>
        )}
      </div>
    </li>
  );
}

// ---------- Form modal ----------

function LicencaFormModal({
  open,
  onClose,
  initial,
  submitting,
  onSubmit,
}: {
  open: boolean;
  onClose: () => void;
  initial?: TenantLicenca | null;
  submitting?: boolean;
  onSubmit: (p: TenantLicencaCreate) => void;
}) {
  const [form, setForm] = useState<TenantLicencaCreate>(() => makeInitial(initial));

  useEffect(() => {
    setForm(makeInitial(initial));
  }, [initial]);

  function set<K extends keyof TenantLicencaCreate>(
    k: K,
    v: TenantLicencaCreate[K] | string,
  ) {
    setForm((f) => ({ ...f, [k]: (v === "" ? null : v) as TenantLicencaCreate[K] }));
  }

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    onSubmit(form);
  }

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={initial ? "Editar licença" : "Nova licença"}
      size="md"
      footer={
        <>
          <Button variant="secondary" onClick={onClose} disabled={submitting}>
            Cancelar
          </Button>
          <Button type="submit" form="lic-form" loading={submitting}>
            {initial ? "Salvar" : "Adicionar"}
          </Button>
        </>
      }
    >
      <form id="lic-form" onSubmit={handleSubmit} className="space-y-4">
        <Field label="Tipo *">
          <Select
            value={form.tipo}
            onChange={(e) => set("tipo", e.target.value as TipoLicenca)}
            options={TIPO_OPTIONS}
            disabled={!!initial}                  // tipo imutável após criar
            required
          />
        </Field>

        <div className="grid gap-4 sm:grid-cols-2">
          <Field label="Número *">
            <Input
              value={form.numero}
              onChange={(e) => set("numero", e.target.value)}
              required
              placeholder="Ex: 12345.678901/2025-99"
            />
          </Field>
          <Field label="Órgão emissor">
            <Input
              value={form.orgao_emissor || ""}
              onChange={(e) => set("orgao_emissor", e.target.value)}
              placeholder="ANVISA, Vigilância SP..."
            />
          </Field>
        </div>

        <div className="grid gap-4 sm:grid-cols-2">
          <Field label="Data de emissão">
            <Input
              type="date"
              value={form.data_emissao || ""}
              onChange={(e) => set("data_emissao", e.target.value || null)}
            />
          </Field>
          <Field
            label={
              <>
                Data de validade
                <Tip label="Data de validade">
                  A partir de 60 dias antes do vencimento o sistema passa a avisar
                  automaticamente.
                </Tip>
              </>
            }
          >
            <Input
              type="date"
              value={form.data_validade || ""}
              onChange={(e) => set("data_validade", e.target.value || null)}
            />
          </Field>
        </div>

        <Field label="Observações">
          <textarea
            className="input min-h-[60px]"
            value={form.observacoes || ""}
            onChange={(e) => set("observacoes", e.target.value)}
            placeholder="Notas internas, restrições, link pra documento..."
          />
        </Field>
      </form>
    </Modal>
  );
}

// ---------- Helpers ----------

function makeInitial(source: TenantLicenca | null | undefined): TenantLicencaCreate {
  if (source) {
    return {
      tipo: source.tipo,
      numero: source.numero,
      orgao_emissor: source.orgao_emissor,
      data_emissao: source.data_emissao,
      data_validade: source.data_validade,
      observacoes: source.observacoes,
    };
  }
  return {
    tipo: "afe",
    numero: "",
    orgao_emissor: null,
    data_emissao: null,
    data_validade: null,
    observacoes: null,
  };
}

function daysUntilExpiry(iso: string | null): number | null {
  if (!iso) return null;
  const validade = new Date(iso);
  if (isNaN(validade.getTime())) return null;
  const today = new Date();
  // Zera horas pra contar dias corridos
  const validadeDay = new Date(validade.getFullYear(), validade.getMonth(), validade.getDate());
  const todayDay    = new Date(today.getFullYear(), today.getMonth(), today.getDate());
  return Math.round((validadeDay.getTime() - todayDay.getTime()) / (1000 * 60 * 60 * 24));
}

function classifyValidity(days: number | null): {
  label: string | null;
  tone: "default" | "success" | "warning" | "danger" | "brand" | "neutral";
  textClass: string;
  borderClass: string;
} {
  if (days === null) return {
    label: null, tone: "default", textClass: "", borderClass: "border-surface-200 dark:border-slate-800",
  };
  if (days < 0) return {
    label: "Vencida", tone: "danger", textClass: "text-red-700 dark:text-red-300",
    borderClass: "border-red-300 dark:border-red-900/40 bg-red-50 dark:bg-red-950/30",
  };
  if (days <= 15) return {
    label: "Crítico", tone: "danger", textClass: "text-red-700 dark:text-red-300",
    borderClass: "border-red-200 dark:border-red-900/40 bg-red-50/40 dark:bg-red-950/20",
  };
  if (days <= 30) return {
    label: "Atenção", tone: "warning", textClass: "text-amber-700 dark:text-amber-300",
    borderClass: "border-amber-200 dark:border-amber-900/40 bg-amber-50/40 dark:bg-amber-950/20",
  };
  if (days <= 60) return {
    label: "Renovar em breve", tone: "default", textClass: "text-slate-600 dark:text-slate-400",
    borderClass: "border-surface-200 dark:border-slate-800",
  };
  return { label: null, tone: "default", textClass: "", borderClass: "border-surface-200 dark:border-slate-800" };
}
